import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { ProductCatalogService } from "../../../../services/api/productCatalog.service";

interface AuditRecord {
  id: string;
  productId: string;
  action: string;
  updatedBy: string;
  updatedOn: string;
  updationRemarks: string | null;
}

const ACTION_STYLES: Record<string, string> = {
  CREATE: "bg-emerald-50 text-emerald-700",
  UPDATE: "bg-blue-50 text-blue-700",
  ACTIVE_TOGGLE: "bg-amber-50 text-amber-700",
  STATUS_CHANGE: "bg-amber-50 text-amber-700",
  COMPOSITION_REPLACE: "bg-purple-50 text-purple-700",
  CATEGORY_REMAP: "bg-indigo-50 text-indigo-700",
};

/**
 * The product's audit trail, newest first.
 *
 * Every V3 write appends one row in the same transaction as the change, so this
 * is a complete record of who touched the product and why. `updatedBy` reads
 * "SYSTEM" when no actor could be identified for the call.
 */
export function ProductVerificationHistoryModal({
  productId,
  onClose,
}: {
  productId: string;
  onClose: () => void;
}) {
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await ProductCatalogService.getVerificationHistory(productId);
      if (cancelled) return;
      setRecords(res?.success ? res.data || [] : []);
      setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-800">
            Verification History
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading history...</p>
          ) : records.length === 0 ? (
            <p className="text-sm text-gray-500">
              No changes recorded for this product yet.
            </p>
          ) : (
            <ol className="space-y-3">
              {records.map((record) => (
                <li
                  key={record.id}
                  className="border border-gray-200 rounded-lg px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        ACTION_STYLES[record.action] ||
                        "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {record.action.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(record.updatedOn).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">
                    By{" "}
                    <span className="font-medium">
                      {record.updatedBy === "SYSTEM"
                        ? "System (no actor recorded)"
                        : record.updatedBy}
                    </span>
                  </p>
                  {record.updationRemarks && (
                    <p className="text-sm text-gray-500 mt-1">
                      {record.updationRemarks}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
